/**
 * Organization Switcher
 * 
 * Dropdown in the sidebar header to switch between organizations.
 */

import { useState, useRef } from 'react';
import { Building2, ChevronDown, Plus, Settings } from 'lucide-react';
import clsx from 'clsx';
import { useOrganization } from '@/context/OrganizationContext';
import { useClickOutside } from '@/hooks/useClickOutside';
import type { Organization } from '@/db/organizations';

interface OrgSwitcherProps {
  onCreateOrg: () => void;
  onOrgSettings: () => void;
}

export function OrgSwitcher({ onCreateOrg, onOrgSettings }: OrgSwitcherProps) {
  const { organizations, currentOrg, setCurrentOrg } = useOrganization();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useClickOutside([dropdownRef], () => setIsOpen(false));

  const handleSelect = (org: Organization) => {
    setCurrentOrg(org);
    setIsOpen(false);
  };

  if (!currentOrg && organizations.length === 0) {
    return (
      <button
        onClick={onCreateOrg}
        className="flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs font-medium text-text-tertiary hover:text-text-primary hover:bg-surface-hover transition-colors w-full"
      >
        <Plus className="w-3.5 h-3.5" />
        <span>Crea organizzazione</span>
      </button>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2.5 px-1 py-1 rounded-lg text-text-primary hover:bg-surface-hover transition-colors min-w-0"
      >
        <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-accent-violet to-accent-blue flex items-center justify-center shadow-sm flex-shrink-0">
          {currentOrg?.avatar_url ? (
            <img src={currentOrg.avatar_url} alt="" className="w-7 h-7 rounded-lg object-cover" />
          ) : (
            <span className="text-[10px] font-bold text-white">{currentOrg?.name?.[0]?.toUpperCase() ?? 'Z'}</span>
          )}
        </div>
        <span className="text-[14px] font-semibold truncate">{currentOrg?.name || 'Organizzazione'}</span>
        <ChevronDown className={clsx('w-3.5 h-3.5 text-text-quaternary transition-transform flex-shrink-0', isOpen && 'rotate-180')} />
      </button>

      {isOpen && (
        <div className="absolute left-0 right-0 top-full mt-1 bg-surface-secondary border border-border-primary rounded-lg shadow-xl z-50 py-1 animate-fade-in">
          {currentOrg && (
            <button
              onClick={() => { onOrgSettings(); setIsOpen(false); }}
              className="flex items-center gap-2 px-3 py-2 w-full text-left text-xs text-text-tertiary hover:bg-surface-hover hover:text-text-primary transition-colors whitespace-nowrap"
            >
              <Settings className="w-3.5 h-3.5 flex-shrink-0" />
              <span>Impostazioni</span>
            </button>
          )}

          <div className="border-t border-border-primary my-1" />

          {organizations.map((org) => (
            <button
              key={org.id}
              onClick={() => handleSelect(org)}
              className={clsx(
                'flex items-center gap-2 px-3 py-2 w-full text-left text-xs transition-colors',
                org.id === currentOrg?.id
                  ? 'bg-accent-violet/10 text-accent-violet font-medium'
                  : 'text-text-secondary hover:bg-surface-hover hover:text-text-primary'
              )}
            >
              <div className="w-4 h-4 rounded bg-accent-violet/20 flex items-center justify-center flex-shrink-0">
                <Building2 className="w-2.5 h-2.5 text-accent-violet" />
              </div>
              <span className="truncate">{org.name}</span>
            </button>
          ))}

          <div className="border-t border-border-primary my-1" />

          <button
            onClick={() => { onCreateOrg(); setIsOpen(false); }}
            className="flex items-center gap-2 px-3 py-2 w-full text-left text-xs text-text-tertiary hover:bg-surface-hover hover:text-text-primary transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            <span>Nuova organizzazione</span>
          </button>
        </div>
      )}
    </div>
  );
}
